class Posting {
  constructor (amount, currency, account, elided, value, comment, valueCurrency) {
    this.amount = amount
    this.currency = currency
    this.account = account
    this.elided = elided || false
    this.value = typeof value === 'undefined' ? null : value
    this.valueCurrency = typeof valueCurrency === 'undefined' ? null : valueCurrency
    this.comment = comment

    if (!this.account) {
      throw new Error(`No account supplied: ${amount} ${currency} ${elided} ${value}`)
    }
  }
}

const parseDynamicAmount = (str) => {
  let currency
  let amount
  let lastAction

  str = str.slice(1, str.length - 1)

  str.split(/\s/).forEach((item) => {
    const possibleCurrency = item.replace(/\d|,|\.|-/g, '').trim()
    const possibleNumber = item.replace(/[^\d-.]/g, '').trim()

    if (item === '+' || item === '-' || item === '/' || item === '*') {
      lastAction = item
      return
    }

    if (possibleCurrency) {
      currency = possibleCurrency

      if (!possibleNumber.length) { return }
    }

    const num = parseFloat(possibleNumber)

    if (!lastAction) {
      amount = num
    } else if (lastAction === '+') {
      amount = amount + num
    } else if (lastAction === '-') {
      amount = amount - num
    } else if (lastAction === '/') {
      amount = amount / num
    } else if (lastAction === '*') {
      amount = num * amount
    }
  })

  return {
    amount,
    currency
  }
}

// A posting's commodity can carry a price annotation pricing it in another
// commodity: "@ <unit price>" prices each unit, "@@ <total>" prices the
// whole posting -- e.g. "-10000.00 USD @@ 14293.74 CAD" is a USD posting
// whose real value, at the rate on the day, was 14293.74 CAD.
const parsePrice = (rawCommodity, atIndex, amount) => {
  const isTotalPrice = rawCommodity[atIndex + 1] === '@'
  const priceStr = rawCommodity.slice(atIndex + (isTotalPrice ? 2 : 1)).trim()
  const priceSplat = priceStr.split(/\s|\t/).filter(Boolean)

  if (priceSplat.length !== 2) return { value: null, valueCurrency: null }

  const price = parseFloat(priceSplat[0])

  // A "@@" total price is always written positive, but the posting it
  // prices balances the transaction with the same sign as its own amount
  // (an elided leg infers from it), so the equivalent value must carry
  // that sign too -- "-10000 USD @@ 14293.74 CAD" is worth -14293.74 CAD.
  return {
    value: isTotalPrice ? Math.abs(price) * Math.sign(amount || 0) : price * amount,
    valueCurrency: priceSplat[1]
  }
}

const parseDetails = (line, transaction, processor) => {
  let currency
  let amount
  let value = null
  let valueCurrency = null

  const commentSplat = line.split(';')
  const accountSplat = commentSplat[0].split(/\s\s+|\t/)
  const account = accountSplat[0].trim()

  if (accountSplat[1]) {
    const rawCommodity = accountSplat[1]
    const atIndex = rawCommodity.indexOf('@')
    const baseStr = (atIndex === -1 ? rawCommodity : rawCommodity.slice(0, atIndex)).trim()
    const commoditySplat = baseStr.split(/\s|\t/)

    if (commoditySplat.length === 1) {
      currency = (commoditySplat[0] || '').replace(/\d|,|\.|-/g, '').trim()
      amount = parseFloat(commoditySplat[0].replace(/[^\d-.]/g, '').trim())
    } else if (commoditySplat.length === 2) {
      amount = parseFloat(commoditySplat[0])
      currency = commoditySplat[1]
    } else {
      const dynamicDetails = parseDynamicAmount(commoditySplat.join(' '))
      amount = parseFloat(dynamicDetails.amount)
      currency = dynamicDetails.currency
    }

    if (atIndex !== -1) {
      const price = parsePrice(rawCommodity, atIndex, amount)
      value = price.value
      valueCurrency = price.valueCurrency
    }
  }

  return {
    account: processor.applyAliasAndCapture(account),
    currency,
    amount,
    value,
    valueCurrency,
    comment: (commentSplat[1] || '').trim()
  }
}

Posting.ELIDED = true
Posting.fromLine = (line, transaction, processor) => {
  const details = parseDetails(line, transaction, processor)

  if (details.currency) {
    return [new Posting(details.amount, details.currency, details.account, null, details.value, details.comment, details.valueCurrency)]
  } else if (details.account) {
    return transaction.buildReconciliation(details.account, details.comment)
  }
}

export default Posting
